import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, getTranslation, SUPPORTED_LANGUAGES } from './translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // iter643 — URL ?lang= takes precedence over localStorage (so hreflang
    // links land on the correct locale even on a fresh browser).
    const supportedCodes = SUPPORTED_LANGUAGES.map(l => l.code);
    try {
      const qp = new URLSearchParams(window.location.search).get('lang');
      if (qp && supportedCodes.includes(qp)) return qp;
    } catch {
      /* SSR / older browser — fall through */
    }
    const saved = localStorage.getItem('nutramilo_language') || 'bg';
    return supportedCodes.includes(saved) ? saved : 'bg';
  });

  useEffect(() => {
    localStorage.setItem('nutramilo_language', language);
    // Set document direction for RTL languages
    const langConfig = SUPPORTED_LANGUAGES.find(l => l.code === language);
    document.documentElement.dir = langConfig?.dir || 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => getTranslation(key, language);

  // pickLang(bg, en, es?) — returns the string for the active language.
  // Falls back to EN when an ES translation is not yet provided.
  const pickLang = (bgText, enText, esText) => {
    if (language === "bg") return bgText;
    if (language === "es") return esText !== undefined && esText !== null ? esText : enText;
    return enText;
  };

  const toggleLanguage = () => {
    const codes = SUPPORTED_LANGUAGES.map(l => l.code);
    const idx = codes.indexOf(language);
    const next = codes[(idx + 1) % codes.length];
    setLanguage(next);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, pickLang, SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};
